import React from "react";

/**
 * Loading placeholder for a dashboard widget.
 *
 * Renders pulsing bars whose bounding box matches the widget's final size so
 * the page does not reflow between loading → ready transitions
 * (per design.md §Widget state contracts and Requirement 3.5).
 *
 * The default layout is a KPI-tile shape: one large "number" bar and one
 * small "caption" bar. `height` should be tuned by the calling widget to
 * match its final rendered height.
 */
export interface WidgetSkeletonProps {
  /** Total height in px reserved for the skeleton. Defaults to 140. */
  height?: number;
}

export function WidgetSkeleton({
  height = 140,
}: WidgetSkeletonProps): JSX.Element {
  return (
    <div
      role="status"
      aria-label="Loading widget"
      aria-busy="true"
      data-testid="widget-skeleton"
      className="w-full flex flex-col justify-center gap-3 px-4"
      style={{ height }}
    >
      {/* Big number-shaped bar */}
      <div className="animate-pulse bg-gray-200 rounded h-8 w-2/3" />
      {/* Small caption-shaped bar */}
      <div className="animate-pulse bg-gray-200 rounded h-3 w-1/3" />
    </div>
  );
}
